import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { TAB_QUERY } from 'src/utils/common-types';
import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getLoanAgreements, fetchAllLoanAgreements } from 'src/redux/slices/loan-agreement';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  useTable,
  TableNoData,
  TableSkeleton,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import {
  LOAN_STATUS_TABS,
  ILoanTableSort,
  ILoanTableFilters,
  LoanAgreementStatus,
  ILoanTableFilterValue,
  ILoanAgreementListItem,
  LOAN_BORROWER_TYPE_OPTIONS,
} from 'src/types/loan-agreement';

import { printLoanAgreements } from '../print-loan-agreements';
import LoanAgreementTableRow from '../loan-agreement-table-row';
import LoanAgreementTableToolbar from '../loan-agreement-table-toolbar';

const TABLE_HEAD = [
  { id: 'displayName', label: 'Borrower', align: 'left', primary: true },
  { id: 'capitalInCents', label: 'Loan Capital', align: 'right', primary: true },
  { id: 'settlementDate', label: 'Settlement Date', align: 'left', primary: true },
  { id: 'paid', label: 'Paid', align: 'left', primary: true },
  { id: '', primary: true },
];

const SORTABLE = ['capitalInCents', 'settlementDate', 'loanPaidDate', 'createdAt', 'updatedAt'];

const defaultFilters: ILoanTableFilters = {
  generalSearch: '',
  borrowerType: 'all',
  overdue: false,
};

// Most recently updated first (a payment, edit or status change all bump
// updatedAt), so whatever was just touched sits at the top.
const defaultSort: ILoanTableSort = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function LoanAgreementListView() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get(TAB_QUERY) as LoanAgreementStatus | null;
  const statusTab =
    tabParam && Object.values(LoanAgreementStatus).includes(tabParam)
      ? tabParam
      : LoanAgreementStatus.ACTIVE;

  const { status, agreements, error } = useAppSelector((state) => state.loanAgreement);

  const { user: authUser } = useAuthContext();

  const settings = useSettingsContext();

  const dispatch = useAppDispatcher();

  const table = useTable({ defaultRowsPerPage: 25, defaultDense: true });

  const { page, rowsPerPage } = table;

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSort);

  const [printing, setPrinting] = useState(false);

  const canReset = !isEqual(defaultFilters, filters);

  const handleFilters = useCallback(
    (name: string, value: ILoanTableFilterValue) => {
      table.onResetPage();
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    table.onResetPage();
    setFilters(defaultFilters);
  }, [table]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      table.onResetPage();
      setSearchParams({ [TAB_QUERY]: value });
      // "Overdue" only applies to active agreements.
      setFilters((prev) => (prev.overdue ? { ...prev, overdue: false } : prev));
    },
    [setSearchParams, table]
  );

  const queryParams = {
    status: statusTab,
    borrowerType: filters.borrowerType === 'all' ? null : filters.borrowerType,
    generalSearch: filters.generalSearch,
    overdue: statusTab === LoanAgreementStatus.ACTIVE && filters.overdue,
    sortBy: sort.sortBy,
    sort: sort.order,
  };

  useEffect(() => {
    dispatch(getLoanAgreements({ page, limit: rowsPerPage, ...queryParams }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, rowsPerPage, statusTab, filters, sort]);

  const handlePrint = async () => {
    // Opened synchronously in the click handler so pop-up blockers allow it.
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      enqueueSnackbar('Allow pop-ups for this site to print', { variant: 'error' });
      return;
    }
    printWindow.document.write('<p style="font-family:Arial">Preparing…</p>');

    setPrinting(true);
    try {
      const rows = await fetchAllLoanAgreements(queryParams);
      const tabLabel = LOAN_STATUS_TABS.find((tab) => tab.value === statusTab)?.label;
      const filterNotes = [
        filters.borrowerType !== 'all' &&
          `Borrower: ${LOAN_BORROWER_TYPE_OPTIONS.find(
            (option) => option.value === filters.borrowerType
          )?.label}`,
        filters.generalSearch && `Search: "${filters.generalSearch}"`,
        queryParams.overdue && 'Overdue only',
      ].filter(Boolean) as string[];

      printLoanAgreements(printWindow, rows, {
        title: `Loan Agreements — ${tabLabel}`,
        filters: filterNotes,
      });
    } catch (e) {
      printWindow.close();
      enqueueSnackbar(e?.message ?? 'Failed to load agreements for printing', {
        variant: 'error',
      });
    } finally {
      setPrinting(false);
    }
  };

  const denseHeight = table.dense ? 60 : 80;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Loan Agreements"
        links={[]}
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:printer-minimalistic-bold" />}
              onClick={handlePrint}
              disabled={printing}
            >
              Print
            </Button>

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_LOAN_AGREEMENT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.loans.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Agreement
              </Button>
            )}
          </Stack>
        }
        sx={{ mb: { xs: 3 } }}
      />

      <Card>
        <Tabs
          value={statusTab}
          onChange={handleTabChange}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {LOAN_STATUS_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <LoanAgreementTableToolbar
          filters={filters}
          onFilters={handleFilters}
          onResetFilters={handleResetFilters}
          canReset={canReset}
          showOverdueFilter={statusTab === LoanAgreementStatus.ACTIVE}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '520px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 880 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={agreements.items.length}
                numSelected={0}
                onSort={(id) => {
                  if (!SORTABLE.includes(id)) return;
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({ sortBy: id, order: 'asc' });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(5)].map((_, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {agreements.items.map((row: ILoanAgreementListItem) => (
                      <LoanAgreementTableRow key={row._id} row={row} />
                    ))}
                  </>
                )}

                {status !== 'failure' && status !== 'loading' && agreements.count === 0 && (
                  <TableNoData message="No loan agreements found" notFound />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          dense={table.dense}
          count={agreements.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
